import { useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
  Grid,
  GridItem,
  Tag,
  SimpleGrid,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { MdFileDownload } from 'react-icons/md';

import { exportResultsToExcel } from './utils';

const TestResultsSummary = ({ summary, companyId }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  if (!summary) {
    return (
      <Box p={4} borderRadius="md" bg="gray.50" mb={4}>
        <Text>אין נתונים להצגה</Text>
      </Box>
    );
  }

  const { general, registeredSummary, notRegisteredSummary } = summary;

  const failedIntegrationsOfRegistered = registeredSummary.integrationsData.find((item) =>
    item.hasOwnProperty('failedCount'),
  );

  const failedIntegrationsOfNotRegistered = notRegisteredSummary.integrationsData.find((item) =>
    item.hasOwnProperty('failedCount'),
  );

  const handleDownloadResults = async () => {
    setIsDownloading(true);

    await new Promise((resolve) => {
      setTimeout(() => {
        exportResultsToExcel(summary, companyId);
        resolve();
      }, 300);
    });

    setIsDownloading(false);
  };

  return (
    <>
      {/* General Summary */}
      <Box mb={6} bg="gray.50" p={4} borderRadius="md">
        <Flex justifyContent="center" alignItems="center" mb={3}>
          <Text fontWeight="bold" fontSize="lg" textAlign="center">
            סיכום כללי
          </Text>
        </Flex>
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          <Stat textAlign="center">
            <StatLabel>סה"כ משתמשים</StatLabel>
            <StatNumber>{general.totalCount}</StatNumber>
          </Stat>
          <Stat textAlign="center">
            <StatLabel>רשומים</StatLabel>
            <StatNumber>{general.registeredCount}</StatNumber>
            <StatHelpText>{general.registeredPercentage}%</StatHelpText>
          </Stat>
          <Stat textAlign="center">
            <StatLabel>לא רשומים</StatLabel>
            <StatNumber>{general.notRegisteredCount}</StatNumber>
            <StatHelpText>{general.notRegisteredPercentage}%</StatHelpText>
          </Stat>
          <Stat textAlign="center">
            <StatLabel>הורד תוצאות</StatLabel>
            <StatNumber>
              <Tooltip label="הורדת תוצאות כקובץ אקסל" placement="bottom">
                <IconButton
                  icon={<MdFileDownload />}
                  colorScheme="blue"
                  size="sm"
                  onClick={handleDownloadResults}
                  isLoading={isDownloading}
                />
              </Tooltip>
            </StatNumber>
          </Stat>
        </SimpleGrid>
      </Box>

      <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={6}>
        {/* Registered Users Summary */}
        <GridItem colSpan={{ base: 2, md: 1 }}>
          <Box bg="blue.50" p={4} borderRadius="md" height="100%">
            <Text fontWeight="bold" fontSize="lg" mb={3} textAlign="center">
              משתמשים רשומים
            </Text>
            <Divider mb={3} />

            <Box mb={3}>
              <Text fontWeight="bold" mb={1}>
                חלוקה לפי חברות:
              </Text>
              <Flex flexWrap="wrap" gap={2}>
                {registeredSummary.companyData.map((company, idx) => (
                  <Tag key={idx} size="md" colorScheme="blue" variant="solid">
                    {company.companyId === 'null' ? 'ללא חברה' : `חברה ${company.companyId}`}:{' '}
                    {company.count} ({company.percentage}%)
                  </Tag>
                ))}
              </Flex>
            </Box>

            <Box>
              <Text fontWeight="bold" mb={1}>
                אינטגרציות:
              </Text>
              <Text>
                נכשלו: {failedIntegrationsOfRegistered?.failedCount ?? 0} (
                {failedIntegrationsOfRegistered?.failedPercentage ?? '0.00'}%)
              </Text>
              <Text>
                עם שעות עבודה: {registeredSummary.withWorkingHoursCount} (
                {registeredSummary.withWorkingHoursPercentage}%)
              </Text>
            </Box>
          </Box>
        </GridItem>

        {/* Not Registered Users Summary */}
        <GridItem colSpan={{ base: 2, md: 1 }}>
          <Box bg="red.50" p={4} borderRadius="md" height="100%">
            <Text fontWeight="bold" fontSize="lg" mb={3} textAlign="center">
              משתמשים לא רשומים
            </Text>
            <Divider mb={3} />

            <Box mb={3}>
              <Text fontWeight="bold" mb={1}>
                חלוקה לפי מעסיקים:
              </Text>
              <Flex flexWrap="wrap" gap={2}>
                {notRegisteredSummary.employerData.map((employer, idx) => (
                  <Tag key={idx} size="md" colorScheme="red" variant="solid">
                    {employer.employerId === 'null' ? 'ללא מעסיק' : `מעסיק ${employer.employerId}`}:{' '}
                    {employer.count} ({employer.percentage}%)
                  </Tag>
                ))}
              </Flex>
            </Box>

            <Box>
              <Text fontWeight="bold" mb={1}>
                נתונים נוספים:
              </Text>
              <Text>
                עם שגיאות: {notRegisteredSummary.withErrorsCount} (
                {notRegisteredSummary.withErrorsPercentage}%)
              </Text>
              <Text>
                עם שעות עבודה: {notRegisteredSummary.withWorkingHoursCount} (
                {notRegisteredSummary.withWorkingHoursPercentage}%)
              </Text>
              <Text>
                אינטגרציות נכשלו: {failedIntegrationsOfNotRegistered?.failedCount ?? 0} (
                {failedIntegrationsOfNotRegistered?.failedPercentage ?? '0.00'}%)
              </Text>
            </Box>
          </Box>
        </GridItem>
      </Grid>
    </>
  );
};

export default TestResultsSummary;
